import {useCallback, useEffect, useRef, useState} from "react";
import {
  getSsoConfirmationModalContent,
  ssoExcludeRequiredRolesToSsoUsers,
  SsoConfirmationModalContent,
  SsoFormValues,
  SsoFormikValues,
  formValuesToApiPayload,
} from "../confirmationModalConstants";
import {SSO_MANAGEMENT_FORM_FIELDS, SSO_USERS} from "../constants";

import {useManageSsoConfiguration} from "./useManageSsoConfiguration";

/**
 * Hook that encapsulates all SSO confirmation modal state and logic.
 *
 * Manages: modal open/close, pending values, mutation, and form revert on cancel.
 * Keeps SSOManagementCard clean by centralizing confirmation flow.
 *
 * @param initialValues - Current server state (ssoEnabled, ssoExcludeRequiredRoles)
 */

export const useSsoConfirmationModal = (initialValues: {
  ssoEnabled: boolean;
  ssoExcludeRequiredRoles: string[] | null;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<SsoFormValues | null>(
    null,
  );

  const initialFormValues: SsoFormValues = {
    ssoEnabled: initialValues.ssoEnabled,
    ssoUsers: ssoExcludeRequiredRolesToSsoUsers(
      initialValues.ssoExcludeRequiredRoles,
    ),
  };
  const initialFormValuesRef = useRef(initialFormValues);

  useEffect(() => {
    initialFormValuesRef.current = initialFormValues;
  }, [initialFormValues]);

  const manageMutation = useManageSsoConfiguration();

  const openModal = useCallback((values: SsoFormValues) => {
    setPendingValues(values);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setPendingValues(null);
  }, []);

  const confirm = useCallback(() => {
    if (!pendingValues) return;
    const payload = formValuesToApiPayload(pendingValues);
    manageMutation.mutate(payload, {
      onSettled: () => closeModal(),
    });
  }, [pendingValues, manageMutation, closeModal]);

  const modalContent: SsoConfirmationModalContent | null = pendingValues
    ? getSsoConfirmationModalContent(pendingValues)
    : null;

  const modalProps = {
    isOpen: isModalOpen,
    onRequestClose: closeModal,
    onConfirm: confirm,
    content: modalContent ?? {
      title: "",
      body: "",
      confirmButtonLabel: "",
    },
    isLoading: manageMutation.isLoading,
  };

  const toFormikValues = (v: SsoFormValues): SsoFormikValues => ({
    [SSO_MANAGEMENT_FORM_FIELDS.SSO_ENABLED]: v.ssoEnabled,
    [SSO_MANAGEMENT_FORM_FIELDS.SSO_USERS]: v.ssoUsers ?? SSO_USERS.NO_USERS,
  });

  return {
    modalProps,
    openModal,
    closeModal,
    initialFormikValues: toFormikValues(initialFormValues),
    revertFormToInitial: (setValues: (values: SsoFormikValues) => void) => {
      setValues(toFormikValues(initialFormValuesRef.current));
    },
  };
};
